import { useEffect, useRef, useCallback, useState } from 'react';
import { configApi, ClientConfig } from '../api/configApi';
import { setBypassNavigationGuard } from './useNavigationGuard';

interface UseIdleTimeoutOptions {
  /** Whether the user is authenticated (only track idle when authenticated) */
  isAuthenticated: boolean;
  /** Callback to execute when session expires (e.g., logout) */
  onSessionExpire: () => void;
}

/**
 * Hook that monitors user activity and triggers session expiration
 * after a configurable period of inactivity.
 *
 * Tracks: mouse movement, keyboard input, clicks, touch, scroll
 */
export function useIdleTimeout({ isAuthenticated, onSessionExpire }: UseIdleTimeoutOptions) {
  const timeoutRef = useRef<number | null>(null);
  const [config, setConfig] = useState<ClientConfig | null>(null);
  const [isConfigLoaded, setIsConfigLoaded] = useState(false);

  // Fetch configuration on mount
  useEffect(() => {
    let mounted = true;

    const fetchConfig = async () => {
      try {
        const clientConfig = await configApi.getClientConfig();
        if (mounted) {
          setConfig(clientConfig);
          setIsConfigLoaded(true);
        }
      } catch {
        console.warn('Failed to fetch client config, using default timeout');
        // Default to 2 hours if config fetch fails
        if (mounted) {
          setConfig({ jwtExpirationMinutes: 480, inactivityTimeoutMinutes: 120, enableSerialSearch: false, showViewportSize: false, siteUnderConstruction: false });
          setIsConfigLoaded(true);
        }
      }
    };

    fetchConfig();

    return () => {
      mounted = false;
    };
  }, []);

  const handleSessionExpire = useCallback(() => {
    // FR-170, FR-171: Set bypass flag FIRST (synchronous) to prevent "Leave site?" dialog
    // This must happen before any navigation to ensure beforeunload doesn't fire
    setBypassNavigationGuard(true);

    // Store current URL to redirect back after re-login
    const currentPath = window.location.pathname;
    if (currentPath !== '/' && currentPath !== '/login' && currentPath !== '/register') {
      sessionStorage.setItem('redirectState', JSON.stringify({ returnUrl: currentPath }));
    }

    // Call the logout callback to clear auth state
    onSessionExpire();

    // FR-170: Use window.location.replace() instead of React Router's navigate()
    // This is SYNCHRONOUS and will start browser navigation immediately,
    // before React can re-render ProtectedRoute (which would redirect to /login)
    window.location.replace('/session-expired');
  }, [onSessionExpire]);

  const resetTimer = useCallback(() => {
    if (!config || !isAuthenticated) return;

    // Clear existing timeout
    if (timeoutRef.current) {
      window.clearTimeout(timeoutRef.current);
    }

    // Set new timeout
    const timeoutMs = config.inactivityTimeoutMinutes * 60 * 1000;
    timeoutRef.current = window.setTimeout(() => {
      handleSessionExpire();
    }, timeoutMs);
  }, [config, isAuthenticated, handleSessionExpire]);

  useEffect(() => {
    if (!isAuthenticated || !isConfigLoaded || !config) {
      // Clear timeout if not authenticated
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      return;
    }

    // Events that indicate user activity
    const activityEvents = [
      'mousedown',
      'mousemove',
      'keydown',
      'scroll',
      'touchstart',
      'click',
      'wheel',
    ];

    // Throttle activity detection to avoid excessive timer resets
    let lastActivity = Date.now();
    const throttleMs = 1000; // Only reset timer once per second max

    const handleActivity = () => {
      const now = Date.now();
      if (now - lastActivity >= throttleMs) {
        lastActivity = now;
        resetTimer();
      }
    };

    // Add event listeners
    activityEvents.forEach(event => {
      document.addEventListener(event, handleActivity, { passive: true });
    });

    // Start initial timer
    resetTimer();

    // Cleanup
    return () => {
      activityEvents.forEach(event => {
        document.removeEventListener(event, handleActivity);
      });
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current);
      }
    };
  }, [isAuthenticated, isConfigLoaded, config, resetTimer]);

  return {
    /** Current configuration (null if not loaded yet) */
    config,
    /** Whether configuration has been loaded */
    isConfigLoaded,
    /** Manually reset the idle timer (e.g., after important actions) */
    resetTimer,
  };
}
